import { useState, useEffect, useCallback } from 'react';
import { rmmAPI } from '@/lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayoutDashboard, MonitorSmartphone, BellRing, KeyRound, ScrollText, ShieldCheck, Zap, FileCode2, Server, Network, DatabaseBackup, ShieldAlert, Layers, Gauge, BookOpenText } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { OverviewTab } from './OverviewTab';
import { DevicesTab } from './DevicesTab';
import { AlertsTab } from './AlertsTab';
import { EnrollmentTab } from './EnrollmentTab';
import { AuditTab } from './AuditTab';
import { PoliciesTab, AutomationTab, ScriptsTab } from './AdvancedTabs';
import { InfrastructureTab } from './InfrastructureTab';
import { NetworkTab } from './NetworkTab';
import { BackupsTab } from './BackupsTab';
import { SecurityTab } from './SecurityTab';
import { BulkTab } from './BulkTab';
import { MSPTab } from './MSPTab';
import { GuideTab } from './GuideTab';
import { Badge } from '@/components/ui/badge';

export default function RMMDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(() => {
    rmmAPI.summary().then((res) => setSummary(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadSummary();
    const t = setInterval(loadSummary, 30000);
    return () => clearInterval(t);
  }, [loadSummary]);

  const tabs = [
    { value: 'overview', label: 'Overview', icon: LayoutDashboard },
    { value: 'devices', label: 'Devices', icon: MonitorSmartphone },
    { value: 'alerts', label: 'Alerts', icon: BellRing, badge: summary?.open_alerts },
    { value: 'infrastructure', label: 'Infra', icon: Server },
    { value: 'network', label: 'Network', icon: Network },
    { value: 'backups', label: 'Backups', icon: DatabaseBackup },
    { value: 'security', label: 'Security', icon: ShieldAlert },
    { value: 'bulk', label: 'Remote Ops', icon: Layers },
    { value: 'msp', label: 'MSP', icon: Gauge },
    { value: 'guide', label: 'How to Use', icon: BookOpenText },
    { value: 'policies', label: 'Policies', icon: ShieldCheck },
    { value: 'automation', label: 'Automation', icon: Zap },
    { value: 'scripts', label: 'Scripts', icon: FileCode2 },
    { value: 'enrollment', label: 'Enrollment', icon: KeyRound },
    ...(isAdmin ? [{ value: 'audit', label: 'Audit Log', icon: ScrollText }] : []),
  ];

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto" data-testid="rmm-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">RMM — Remote Monitoring</h1>
        <p className="text-sm md:text-base text-slate-500">Full MSP platform · monitoring, security, backups, remote management & AI</p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1 mb-6 justify-start">
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value} className="gap-1.5 data-[state=active]:bg-white" data-testid={`rmm-tab-${t.value}`}>
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.badge > 0 && <Badge className="bg-red-500 text-white ml-1 h-5 px-1.5">{t.badge}</Badge>}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview"><OverviewTab summary={summary} onRefresh={loadSummary} /></TabsContent>
        <TabsContent value="devices"><DevicesTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="alerts"><AlertsTab onChanged={loadSummary} /></TabsContent>
        <TabsContent value="infrastructure"><InfrastructureTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="network"><NetworkTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="backups"><BackupsTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="security"><SecurityTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="bulk"><BulkTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="msp"><MSPTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="guide"><GuideTab /></TabsContent>
        <TabsContent value="policies"><PoliciesTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="automation"><AutomationTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="scripts"><ScriptsTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="enrollment"><EnrollmentTab isAdmin={isAdmin} /></TabsContent>
        {isAdmin && <TabsContent value="audit"><AuditTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
